import DB, { ConditionalWriteError, SharedStateResult, SharedStateUserItem } from '../DB';

export type SharedStateWithCounters = SharedStateResult<any, any> & {
    counters: { [counterName: string]: { updatedAt: number; value: number } | undefined };
};

export function createDevDBSharedStates(opts: {
    getAllSharedStateIds: () => { stateId: string; stateName: string }[];
    getSharedState: (opts: { stateId: string; stateName: string }) => SharedStateWithCounters | null;
    setSharedState: (opts: { stateId: string; stateName: string; sharedState: SharedStateWithCounters }) => void;
    deleteSharedState: (opts: { stateId: string; stateName: string }) => void;
}): DB<any>['sharedStates'] {
    const getDefaultSharedState = (): SharedStateWithCounters => ({
        counters: {},
        sharedStateMessages: [],
        userSharedStates: {},
    });

    return {
        counters: {
            fetchCounters: async ({ stateId, stateName }) => {
                const sharedState = opts.getSharedState({ stateId, stateName });

                return sharedState?.counters ?? {};
            },

            setCounter: async ({ counterName, now, stateId, stateName, value }) => {
                const sharedState = opts.getSharedState({ stateId, stateName }) ?? getDefaultSharedState();

                sharedState.counters[counterName] = { updatedAt: now, value };

                opts.setSharedState({ sharedState, stateId, stateName });
            },

            increaseCounters: async ({ counters, now, stateId, stateName }) => {
                const sharedState = opts.getSharedState({ stateId, stateName }) ?? getDefaultSharedState();

                for (const counter in counters) {
                    const oldValue = sharedState.counters[counter]?.value || 0;
                    sharedState.counters[counter] = { updatedAt: now, value: oldValue + counters[counter]! };
                }

                opts.setSharedState({ sharedState, stateId, stateName });
            },
        },

        deleteSharedStateMessages: async (messages) => {
            for (const { messageId, stateId, stateName } of messages) {
                const sharedState = opts.getSharedState({ stateId, stateName });
                if (!sharedState) {
                    continue;
                }

                sharedState.sharedStateMessages = sharedState.sharedStateMessages.filter((msg) => msg.id !== messageId);
                opts.setSharedState({ stateId, stateName, sharedState });
            }
        },

        deleteUserSharedState: async ({ stateId, stateName, userId }) => {
            const sharedState = opts.getSharedState({ stateId, stateName });

            if (!sharedState?.userSharedStates?.[userId]) {
                return;
            }

            delete sharedState.userSharedStates[userId];

            opts.setSharedState({ stateId, stateName, sharedState });
        },

        fetchGlobalSharedState: async ({ stateId, stateName }) =>
            opts.getSharedState({ stateId, stateName })?.globalSharedState ?? null,

        fetchSharedState: async ({ stateId, stateName }) => {
            const sharedState = opts.getSharedState({ stateId, stateName });

            if (!sharedState) {
                return { consumedCapacity: 0, sharedState: null };
            }

            const { counters, ...sharedStateWithoutCounters } = sharedState;

            return { consumedCapacity: 0, sharedState: sharedStateWithoutCounters };
        },

        fetchUserStates: async (userId) => {
            const result: { [stateName: string]: { [stateId: string]: SharedStateUserItem<any, any> } } = {};

            for (const { stateId, stateName } of opts.getAllSharedStateIds()) {
                const userState = opts.getSharedState({ stateId, stateName })?.userSharedStates[userId];
                if (!userState) {
                    continue;
                }

                if (!result[userState.stateName]) {
                    result[userState.stateName] = {};
                }

                result[userState.stateName]![userState.stateId] = userState;
            }

            return result;
        },
        migrateLegacyUserSharedStates: async () => {
            // no-op in development
        },

        writeGlobalSharedState: async (payload) => {
            const sharedState =
                opts.getSharedState({ stateId: payload.stateId, stateName: payload.stateName }) ??
                getDefaultSharedState();

            const currentRev = sharedState.globalSharedState?.rev;
            if (currentRev !== undefined && currentRev !== payload.rev - 1) {
                throw new ConditionalWriteError(payload.rev);
            }

            sharedState.globalSharedState = payload;
            opts.setSharedState({ stateId: payload.stateId, stateName: payload.stateName, sharedState });
        },

        writeSharedStateMessages: async (messages) => {
            for (const message of messages) {
                const sharedState =
                    opts.getSharedState({ stateId: message.stateId, stateName: message.stateName }) ??
                    getDefaultSharedState();

                sharedState.sharedStateMessages.push(message);
                sharedState.sharedStateMessages.sort((a, b) => a.id.localeCompare(b.id));
                opts.setSharedState({ stateId: message.stateId, stateName: message.stateName, sharedState });
            }
        },
        writeUserSharedState: async (payload) => {
            const sharedState =
                opts.getSharedState({ stateId: payload.stateId, stateName: payload.stateName }) ??
                getDefaultSharedState();

            const currentRev = sharedState.userSharedStates[payload.userId]?.rev;
            if (currentRev !== undefined && currentRev !== payload.rev - 1) {
                throw new ConditionalWriteError(payload.rev);
            }

            sharedState.userSharedStates[payload.userId] = payload;
            opts.setSharedState({ stateId: payload.stateId, stateName: payload.stateName, sharedState });
        },
    };
}
